import {getGenreTranslationKey} from "src/app/modules/stats/utils/genre-translation.util";
import {NgClass} from "@angular/common";
import {SINGULAR_WORKS_LABEL_KEYS} from "src/app/enums/Works.enum";
import {sendWorkRecommendationBatches} from "src/app/modules/shared/utils/work-bulk-batches.util";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {isEmpty, isEqual} from "lodash-es";
import {Subscription, of, switchMap} from "rxjs";
import {
  WorksModel,
  SendRecommendationResponse,
} from "src/app/models/WorksModel";
import {Friend} from "src/app/modules/friends/interfaces/friend.interface";
import {FriendsService} from "src/app/modules/friends/services/friends.service";
import {PaginatedResponse} from "src/app/modules/shared/interfaces/ApiResponse.interface";
import {CustomError} from "src/app/modules/shared/interfaces/CustomError.interface";
import {ConfigService} from "src/app/modules/shared/services/config.service";
import {ScreenSizeService} from "src/app/modules/shared/services/screenSize.service";
import {WorksService} from "src/app/modules/shared/services/works.service";
import {environment} from "src/environments/environment";
import {ModalService} from "../../services/modal.service";
import {ToastService} from "../../services/toast.service";
import {User} from "src/app/modules/auth/models/User";
import {AuthService} from "src/app/modules/auth/services/auth.service";
import {UserAvatarComponent} from "src/app/modules/shared/components/user-avatar/user-avatar.component";
import {getTopCompanionFriends} from "src/app/modules/friends/utils/friend-companion-rank.util";
import {ExternalWorkShareComponent} from "./external-work-share.component";
import {ModalComponent} from "../modal/modal.component";
import {AddFriendComponent} from "src/app/modules/friends/components/add-friend/add-friend.component";

@Component({
  selector: "app-send-work",
  templateUrl: "./send-work.component.html",
  styleUrl: "./send-work.component.css",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    UserAvatarComponent,
    ExternalWorkShareComponent,
    ModalComponent,
    AddFriendComponent,
  ],
})
export class SendWorkComponent implements OnInit, OnDestroy {
  readonly getGenreTranslationKey = getGenreTranslationKey;
  @Input() item!: WorksModel;
  @Input() items: WorksModel[] = [];
  @Input() list?: string;
  @Input() modalId?: string;
  @ViewChild("searchInput", {static: false}) searchInput?: ElementRef;
  user!: User;
  public imagesDomain: string = environment.imagesDomain;
  public isMobile = false;

  private readonly searchLimit;
  public recommendationForm: FormGroup;
  public friends: Friend[] = [];
  private rankedFriends: Friend[] = [];
  public friendsLoading = true;
  public friendsError = false;
  searchingFriends = false;
  readonly workTypeLabels = SINGULAR_WORKS_LABEL_KEYS;
  public filteredFriends: Friend[] = [];
  public selectedFriends: Friend[] = [];
  public isClearVisible = false;
  public submitText = "Send recommendation";
  public isSending = false;
  error!: CustomError | null;

  private readonly subscriptions: Subscription[] = [];

  constructor(
    public worksService: WorksService,
    public friendsService: FriendsService,
    private readonly authService: AuthService,
    private readonly configService: ConfigService,
    private readonly modalService: ModalService,
    private readonly toastService: ToastService,
    private readonly screenSizeService: ScreenSizeService,
    private readonly translate: TranslateService,
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef
  ) {
    this.searchLimit = this.configService.searchLimit;

    const userSubscription = this.authService.user$.subscribe({
      next: (user: User | null) => {
        if (user) {
          this.user = user;
          this.cdr.markForCheck();
        }
      },
    });
    this.subscriptions.push(userSubscription);

    this.recommendationForm = this.fb.group({
      inviteUser: [""],
      comment: [""],
    });

    this.subscriptions.push(
      this.screenSizeService.screenSize$.subscribe((size) => {
        this.isMobile = size.isMobile;
        this.cdr.markForCheck();
      })
    );
  }

  ngOnInit() {
    this.fetchFriends();
    this.setSubmitText("SEND_RECOMMENDATION");

    const translateSubscription = this.translate.onLangChange.subscribe(() => {
      this.setSubmitText("SEND_RECOMMENDATION");
      this.cdr.markForCheck();
    });
    this.subscriptions.push(translateSubscription);
  }

  public fetchFriends(page = 1) {
    this.friendsLoading = true;
    this.friendsError = false;
    const friendsSubscription = this.friendsService
      .getFriends(this.user._id, true, true)
      .subscribe({
        next: (response: PaginatedResponse | null) => {
          this.friendsLoading = false;
          if (response?.data && response.data.length > 0) {
            const friends = response.data as Friend[];

            if (page > 1 && this.friends?.length) {
              const offset: number = (page - 1) * this.searchLimit;
              this.friends?.splice(offset, this.searchLimit);
              this.friends = [...this.friends, ...friends];
            } else if (!isEqual(this.friends, friends)) {
              this.friends = [...friends];
            }
          } else {
            this.friends = [];
          }

          this.rankedFriends = getTopCompanionFriends(
            this.friends,
            this.friends.length
          );
          this.cdr.markForCheck();
        },
        error: () => {
          this.friendsLoading = false;
          this.friendsError = true;
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(friendsSubscription);
  }

  private setSubmitText(key: string): void {
    const translateSubscription = this.translate
      .get(key)
      .subscribe((translatedText) => {
        this.submitText = translatedText;
        this.cdr.markForCheck();
      });
    this.subscriptions.push(translateSubscription);
  }

  openModal(modalId: string): void {
    this.modalService.openModal(modalId);
  }

  openFriendSearch(): void {
    this.searchingFriends = true;
    this.openModal("addFriendFromRecommendation");
  }

  closeModal(modalId: string): void {
    this.modalService.closeModal(modalId);
  }

  public onUserSearch(): void {
    if (this.isSending) return;
    const search: string = this.recommendationForm
      .get("inviteUser")
      ?.value.trim()
      .toLowerCase();
    if (!isEmpty(search)) {
      this.isClearVisible = true;
      this.filteredFriends = this.friends.filter(
        (friend: Friend) => friend.username?.toLowerCase().includes(search)
      );
    } else {
      this.isClearVisible = false;
      this.filteredFriends = [];
    }
    this.cdr.markForCheck();
  }

  public selectFriend(friend: Friend): void {
    if (this.isSending) return;
    this.selectedFriends = this.isSelected(friend)
      ? this.selectedFriends.filter((selected) => selected._id !== friend._id)
      : [...this.selectedFriends, friend];
  }

  public get quickFriends(): Friend[] {
    return this.isMobile ? this.rankedFriends : this.rankedFriends.slice(0, 8);
  }

  get friendsLayout(): string {
    if (this.isClearVisible) return "flex flex-col";
    return this.isMobile
      ? "flex gap-0.5 overflow-x-auto py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      : "grid grid-cols-4 gap-x-0.5 gap-y-1 py-1";
  }

  public get visibleFriends(): Friend[] {
    return this.isClearVisible
      ? this.filteredFriends.slice(0, 4)
      : this.quickFriends;
  }

  public isSelected(friend: Friend): boolean {
    return this.selectedFriends.some((selected) => selected._id === friend._id);
  }

  get releaseYear(): number | undefined {
    const release = this.item.releaseDate;
    if (release === undefined) return undefined;
    const year =
      typeof release === "number" ? release : new Date(release).getFullYear();
    return Number.isFinite(year) ? year : undefined;
  }

  public get sendLabel(): string {
    if (this.isSending) return this.submitText;
    if (!this.selectedFriends.length)
      return this.translate.instant("WORK_SHARE.CHOOSE_FRIENDS");
    if (this.selectedFriends.length === 1)
      return this.translate.instant("WORK_SHARE.SEND_ONE", {
        username: this.selectedFriends[0].username,
      });
    return this.translate.instant("WORK_SHARE.SEND_MANY", {
      count: this.selectedFriends.length,
    });
  }

  public clearSearch(): void {
    if (this.isSending) return;
    this.recommendationForm.patchValue({inviteUser: ""});
    this.filteredFriends = [];
    this.isClearVisible = false;
    this.searchInput?.nativeElement.focus();
  }

  onSubmit() {
    if (this.isSending) return;
    const comment: string = this.recommendationForm.get("comment")?.value;
    const receivers: string[] = this.selectedFriends.map((f) => f._id);

    if (receivers.length === 0) {
      console.warn("No friends selected");
      return;
    }

    this.setSubmitText("SENDING");
    this.isSending = true;
    this.error = null;

    const selectedItems = this.selectedItems;
    const ensureWorks$ = of(selectedItems);

    const sendSubscription = ensureWorks$
      .pipe(
        switchMap((works: WorksModel[]) => {
          this.item = works[0] ?? this.item;

          const recommendationDto = {
            works: works.map((work) => ({
              _id: work._id,
              type: work.type,
            })),
            receivers,
            comment,
          };

          return sendWorkRecommendationBatches(recommendationDto, (batch) =>
            this.worksService.sendRecommendationToFriend(this.user._id, batch)
          );
        })
      )
      .subscribe({
        next: (res: SendRecommendationResponse) => {
          for (const result of res.results) {
            let translationKey: string;
            const params: Record<string, string | number> = {
              username: result.username ?? "",
              count: result.successCount,
              total: result.works.length,
            };
            const statuses = result.works.map((work) => work.status);

            if (result.successCount === result.works.length) {
              translationKey = "RECOMMENDATIONS_SENT_TO";
            } else if (result.successCount > 0) {
              translationKey = "RECOMMENDATIONS_PARTIALLY_SENT_TO";
            } else if (statuses.every((status) => status === "alreadySeen")) {
              translationKey = "ALREADY_IN_GEMSTER";
            } else if (
              statuses.every((status) => status === "alreadyRecommended")
            ) {
              translationKey = "ALREADY_RECOMMENDED";
            } else if (
              result.works.some((work) => work.error === "SOCIAL_UNAVAILABLE")
            ) {
              translationKey = "SOCIAL_UNAVAILABLE";
            } else {
              translationKey = "UNKNOWN_ERROR";
            }

            let toastClass: string;
            const hasError = statuses.includes("error");
            if (result.successCount === result.works.length) {
              toastClass = "bg-form-success";
            } else if (result.successCount === 0 && hasError) {
              toastClass = "bg-form-error";
            } else {
              toastClass = "bg-primary";
            }

            const toastSubscription = this.translate
              .get(translationKey, params)
              .subscribe((translatedText) => {
                this.toastService.sendToast({
                  text: translatedText,
                  translatable: false,
                  classes: toastClass,
                });
              });
            this.subscriptions.push(toastSubscription);
          }
          const completedWorkKeys = new Set(
            selectedItems
              .filter((item) =>
                res.results.every((receiver) =>
                  receiver.works.some(
                    (work) =>
                      work.work._id === item._id &&
                      work.work.type === item.type &&
                      work.status !== "error"
                  )
                )
              )
              .map((item) => `${item.type}:${item._id}`)
          );
          this.modalService.bulkActionCompleted.emit({
            references: selectedItems
              .filter((work) =>
                completedWorkKeys.has(`${work.type}:${work._id}`)
              )
              .map((work) => ({_id: work._id, type: work.type})),
            removesFromSource: false,
          });
          this.setSubmitText("SEND_RECOMMENDATION");
          this.isSending = false;
          const successfulRecipients = new Set(
            res.results
              .filter((result) =>
                result.works.every((work) => work.status !== "error")
              )
              .map((result) => result.receiverId)
          );
          this.selectedFriends = this.selectedFriends.filter(
            (friend) => !successfulRecipients.has(friend._id)
          );
          if (this.items.length > 1 && this.selectedFriends.length === 0) {
            this.recommendationForm.patchValue({comment: ""});
            this.closeModal(this.modalId ?? `send_work_${this.item._id}`);
          }
          this.cdr.markForCheck();
        },
        error: (error: CustomError) => {
          const code = error.error?.error ?? "UNKNOWN_ERROR";
          this.error = {
            error: {
              error:
                code === "ACCESS_DENIED_ERROR"
                  ? "RECOMMENDATION_RECIPIENT_UNAVAILABLE"
                  : code,
            },
          };
          this.isSending = false;
          this.setSubmitText("SEND_RECOMMENDATION");
          this.toastService.sendToast({
            text: this.error.error.error,
            classes: "bg-form-error",
          });
          this.cdr.markForCheck();
        },
      });
    this.subscriptions.push(sendSubscription);
  }

  onKeyUp(
    event: KeyboardEvent,
    type: string,
    modalId?: string,
    friend?: Friend
  ): void {
    if (event.key === "Enter" || event.key === " ") {
      if (type === "openSendRecommendationModal" && modalId) {
        this.openModal(modalId);
      } else if (type === "clearSearch") {
        this.clearSearch();
      } else if (type === "selectFriend" && friend) {
        this.selectFriend(friend);
      }
    }
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  private get selectedItems(): WorksModel[] {
    return this.items.length > 0 ? this.items : [this.item];
  }
}
